import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { getDonationForm, getDonationFormBySlug, getDefaultDonationForm, getActiveSubscription, getDonationReceipt, getMemberDetail, getCurrentEnvironment } from '@kuow/core';
import type { MemberDetailEntity } from '@kuow/core';
import { logger } from '@kuow/core';
import type {
  DonationFormContentOverrides,
  DonationFormInitialSelection,
  DonationFormInitialStep,
  DonationFormPaymentOverrides,
  DonationFormState,
  FormAction,
  TierOverrides,
} from '../types';
import { formReducer } from './formReducer';
import {
  setFormDetails,
  setLoading,
  setError,
  setActiveSubscription,
  setDonorInfo,
  setFrequency,
  setAmount,
  setCustomAmount,
  setUtmCampaign,
  setMemberId,
  setCanMakeApplePay,
} from './formActions';
import type { DonationFormMode } from '../types';
import type { DonationForm, DonationResponse, DonationTier } from '@kuow/core';
import { getAmountTiers, isRecord, parsePositiveAmount } from '@kuow/core';
import { canMakeApplePayPayments } from '../applePayHelper';

const BILLING_UPDATE_FORM_SLUG = 'updateinfo';

const createInitialState = (
  mode: DonationFormMode,
  upgradeJwt: string | null,
  utmCampaign: string | null,
  sfmcSubkey: string | null,
  contentOverrides: DonationFormContentOverrides | null,
  memberId: number | null,
  billingUpdateWithAuth: boolean,
  disableCaptcha: boolean,
  placement: string | null
): DonationFormState => ({
  mode,
  upgradeJwt,
  utmCampaign,
  sfmcSubkey,
  placement,
  contentOverrides,
  disableCaptcha,
  activeSubscription: null,
  useNewPaymentMethod: false,
  billingUpdateToken: null,
  billingUpdateWithAuth,
  formDetails: null,
  environment: getCurrentEnvironment(),
  currentStep: 'amount',
  formData: {
    frequency: 'monthly',
    amount: null,
    customAmount: null,
    coverTheCost: false,
    coverTheCostAmount: 0,
    totalDonationAmount: null,
    gift: null,
    noGift: false,
    paymentMethod: null,
    cardData: null,
    bankData: null,
    applePayData: null,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    country: 'United States',
    postalCode: '',
    newsletterOptIn: false,
    captcha: '',
  },
  baseAmount: null,
  baseAmountIsCustom: false,
  validation: {
    amount: false,
    gift: false,
    payment: false,
    information: false,
  },
  isSubmitting: false,
  isLoading: false,
  error: null,
  submissionError: null,
  receipt: null,
  memberId,
  canMakeApplePay: false,
});

interface FormContextValue {
  state: DonationFormState;
  dispatch: React.Dispatch<FormAction>;
  content: { headline: string; description: string | null };
  paymentOverrides?: DonationFormPaymentOverrides;
}

const FormContext = createContext<FormContextValue | undefined>(undefined);

const normalizeOverrideString = (value: string | null | undefined): string | null | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  return value;
};

const resolveHeadline = (state: DonationFormState): string => {
  const override = normalizeOverrideString(state.contentOverrides?.headline);
  if (override === null) return '';
  if (typeof override === 'string') return override;
  return state.formDetails?.headline ?? '';
};

const resolveDescription = (state: DonationFormState): string | null => {
  const override = normalizeOverrideString(state.contentOverrides?.description);
  if (override === null) return null;
  if (typeof override === 'string') return override;
  return state.formDetails?.description ?? null;
};

const normalizeCountry = (country?: string): string => {
  if (!country) return 'United States';
  const normalized = country.trim().toLowerCase();
  if (normalized === 'ca' || normalized === 'can' || normalized === 'canada') return 'Canada';
  return 'United States';
};

const mapSubscriptionToDonorInfo = (subscription: DonationResponse) => {
  const member = subscription.member;
  const shipping = member.shipping_address ?? member.mailing_address;
  return {
    firstName: member.first_name ?? '',
    lastName: member.last_name ?? '',
    email: member.email ?? '',
    phone: member.phone_number ?? '',
    street: shipping?.street ?? '',
    city: shipping?.city ?? '',
    state: shipping?.state ?? '',
    country: normalizeCountry(shipping?.country),
    postalCode: shipping?.postal_code ?? '',
    newsletterOptIn: member.newsletter ?? false,
    captcha: '',
  };
};

const mapMemberDetailToDonorInfo = (member: MemberDetailEntity) => {
  const shipping = member.shipping_address ?? member.mailing_address;
  return {
    firstName: member.first_name ?? '',
    lastName: member.last_name ?? '',
    email: member.email ?? '',
    phone: member.phone_number ?? member.login_phone_number ?? '',
    street: shipping?.street ?? '',
    city: shipping?.city ?? '',
    state: shipping?.state ?? '',
    country: normalizeCountry(shipping?.country),
    postalCode: shipping?.postal_code ?? '',
    newsletterOptIn: member.newsletter ?? false,
    captcha: '',
  };
};

const parseTierOverrides = (input: unknown): DonationTier[] | null => {
  const parsed: unknown = (() => {
    if (typeof input === 'string') {
      try {
        const result: unknown = JSON.parse(input);
        return result;
      } catch {
        return null;
      }
    }
    return input;
  })();

  if (!isRecord(parsed)) return null;
  const donationTiers = parsed.donation_tiers;
  if (!Array.isArray(donationTiers)) return null;
  const donationTiersUnknown: unknown[] = donationTiers;
  if (donationTiersUnknown.length === 0) return null;

  const normalized: DonationTier[] = [];
  let prevMonthly: number | null = null;

  for (let i = 0; i < donationTiersUnknown.length; i++) {
    const item: unknown = donationTiersUnknown[i];
    if (!isRecord(item)) return null;

    const single = item.single_donation_amount;
    const monthly = item.monthly_donation_amount;
    if (typeof single !== 'string' || typeof monthly !== 'string') return null;

    const monthlyNumeric = parsePositiveAmount(monthly);
    const singleNumeric = parsePositiveAmount(single);
    if (monthlyNumeric === null || singleNumeric === null) return null;

    if (prevMonthly !== null && monthlyNumeric <= prevMonthly) return null;
    prevMonthly = monthlyNumeric;

    const id = typeof item.id === 'number' ? item.id : i + 1;
    const tier = typeof item.tier === 'number' ? item.tier : i + 1;
    const flag = typeof item.flag === 'string' || item.flag === null ? item.flag : null;

    normalized.push({
      id,
      tier,
      single_donation_amount: single,
      monthly_donation_amount: monthly,
      flag,
    });
  }

  return normalized;
};

const applyTierOverrides = (formDetails: DonationForm, tierOverrides: TierOverrides): DonationForm => {
  const tiers = parseTierOverrides(tierOverrides);
  if (!tiers) {
    logger.warn('Invalid tierOverrides provided; falling back to API tiers', { formId: formDetails.id });
    return formDetails;
  }

  return { ...formDetails, donation_tiers: tiers };
};

const applyPaymentOverrides = (formDetails: DonationForm, paymentOverrides: DonationFormPaymentOverrides): DonationForm => {
  return {
    ...formDetails,
    paypal: paymentOverrides.paypal ?? formDetails.paypal,
    apple_pay: paymentOverrides.applePay ?? formDetails.apple_pay,
  };
};

type DefaultOrUpgradeProviderFormIdentifier =
  | { formId: string; formSlug?: never; useDefault?: never }
  | { formId?: never; formSlug: string; useDefault?: never }
  | { formId?: never; formSlug?: never; useDefault: true };

type DefaultOrUpgradeProviderProps = DefaultOrUpgradeProviderFormIdentifier & {
  mode?: Exclude<DonationFormMode, 'billing-update'>;
  upgradeJwt?: string;
  children: React.ReactNode;
  utmCampaign: string | null;
  sfmcSubkey?: string | null;
  /** GA4 donatePlacement for this form's events. */
  placement?: string;
  contentOverrides?: DonationFormContentOverrides | null;
  tierOverrides?: TierOverrides;
  paymentOverrides?: DonationFormPaymentOverrides;
  preview?: boolean;
  initialSelection?: DonationFormInitialSelection;
  initialStep?: DonationFormInitialStep;
  /**
   * When true, skip showing/executing reCAPTCHA.
   */
  disableCaptcha?: boolean;
  /**
   * When provided, the donation will be associated with this existing member (admin flow).
   */
  memberId?: number;
  /**
   * When provided, donor info is pre-populated from this object instead of fetching
   * `/donate/v1/members/:id` (which is admin-only). Used by signed-in self-service flows
   * where the auth context already has the full member detail.
   */
  prefillDonor?: MemberDetailEntity | null;
};

interface BillingUpdateProviderProps {
  mode: 'billing-update';
  receiptNumber: string;
  /** Required for public billing update flow; omit for admin flow with withAuth */
  updateToken?: string;
  /** When true, uses authenticated session instead of updateToken (admin flow) */
  withAuth?: boolean;
  children: React.ReactNode;
  formId?: string;
  utmCampaign: string | null;
  sfmcSubkey?: string | null;
  contentOverrides?: DonationFormContentOverrides | null;
  paymentOverrides?: DonationFormPaymentOverrides;
  /** Starting step for billing update flow. Defaults to 'payment' for public, 'amount' recommended for admin */
  initialStep?: 'amount' | 'payment';
  /**
   * When true, skip showing/executing reCAPTCHA.
   */
  disableCaptcha?: boolean;
}

export type FormProviderProps = DefaultOrUpgradeProviderProps | BillingUpdateProviderProps;

export const FormProvider: React.FC<FormProviderProps> = (props) => {
  const mode: DonationFormMode = props.mode ?? 'default';
  const isBillingUpdate = props.mode === 'billing-update';

  const formId = isBillingUpdate ? props.formId ?? '' : ('formId' in props ? props.formId : undefined);
  const formSlug = isBillingUpdate ? undefined : ('formSlug' in props ? props.formSlug : undefined);
  const useDefault = isBillingUpdate ? false : ('useDefault' in props ? props.useDefault : false);
  const formKey = formId ?? formSlug ?? (useDefault ? 'default' : '');
  const upgradeJwt = isBillingUpdate ? undefined : props.upgradeJwt;
  const utmCampaign = props.utmCampaign;
  const sfmcSubkey = props.sfmcSubkey ?? null;
  const placement = isBillingUpdate ? null : (props.placement ?? null);
  const tierOverrides = isBillingUpdate ? undefined : props.tierOverrides;
  const paymentOverrides = isBillingUpdate ? props.paymentOverrides : ('paymentOverrides' in props ? props.paymentOverrides : undefined);
  const initialSelection = isBillingUpdate ? undefined : props.initialSelection;
  const initialStep = isBillingUpdate ? props.initialStep : ('initialStep' in props ? props.initialStep : undefined);
  const memberId = isBillingUpdate ? undefined : props.memberId;
  const prefillDonor = isBillingUpdate ? null : (props.prefillDonor ?? null);
  const preview = isBillingUpdate ? false : Boolean('preview' in props ? props.preview : false);
  const receiptNumber = isBillingUpdate ? props.receiptNumber : undefined;
  const updateToken = isBillingUpdate ? props.updateToken : undefined;
  const billingUpdateWithAuth = isBillingUpdate ? props.withAuth ?? false : false;
  const billingUpdateInitialStep = isBillingUpdate ? (props.initialStep ?? 'payment') : undefined;
  const contentOverrides = 'contentOverrides' in props ? props.contentOverrides : undefined;
  const disableCaptcha = props.disableCaptcha ?? false;
  const { children } = props;

  const [state, dispatch] = useReducer(formReducer, undefined, () =>
    createInitialState(
      mode,
      upgradeJwt ?? null,
      utmCampaign ?? null,
      sfmcSubkey,
      contentOverrides ?? null,
      memberId ?? null,
      billingUpdateWithAuth,
      disableCaptcha,
      placement
    )
  );

  useEffect(() => {
    if (mode === 'billing-update') {
      return;
    }

    const loadFormDetails = async () => {
      dispatch(setLoading(true));
      try {
        let formDetails: DonationForm;
        if (formId) {
          formDetails = await getDonationForm(formId);
        } else if (formSlug) {
          formDetails = await getDonationFormBySlug(formSlug, { preview });
        } else if (useDefault) {
          formDetails = await getDefaultDonationForm({ preview });
        } else {
          throw new Error('No form identifier provided');
        }

        let formDetailsWithOverrides = formDetails;
        if (mode === 'default' && tierOverrides) {
          formDetailsWithOverrides = applyTierOverrides(formDetailsWithOverrides, tierOverrides);
        }
        if (paymentOverrides) {
          formDetailsWithOverrides = applyPaymentOverrides(formDetailsWithOverrides, paymentOverrides);
        }
        dispatch(setFormDetails(formDetailsWithOverrides));

        if (mode === 'default' && initialSelection) {
          dispatch(setFrequency(initialSelection.frequency));
          dispatch(setAmount(null));
          dispatch(setCustomAmount(null));

          if (initialSelection.kind === 'tier') {
            const tierItems = getAmountTiers(formDetailsWithOverrides, initialSelection.frequency, { limit: 4 });
            const selectedTier = tierItems[initialSelection.tierIndex];
            if (selectedTier) {
              dispatch(setAmount(selectedTier.numericAmount));
            }
          } else if (initialSelection.kind === 'custom') {
            dispatch(setCustomAmount(initialSelection.amount));
          }

          if (initialStep === 'payment') {
            dispatch({ type: 'SET_STEP', payload: 'payment' });
          }

          if (initialStep === 'gift' && formDetailsWithOverrides.gifts.length > 0) {
            dispatch({ type: 'SET_STEP', payload: 'gift' });
          }
        }

        if (mode !== 'monthly-upgrade') {
          dispatch(setLoading(false));
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load donation form';
        logger.error('Failed to load donation form', { formKey, formId, formSlug, useDefault, error: err });
        dispatch(setError(errorMessage));
      }
    };

    void loadFormDetails();
  }, [formId, formSlug, useDefault, mode, tierOverrides, paymentOverrides, initialSelection, initialStep, preview, dispatch]);

  useEffect(() => {
    const loadSubscription = async () => {
      if (mode !== 'monthly-upgrade') return;
      if (!upgradeJwt) {
        dispatch(setError('Missing upgrade token'));
        return;
      }

      dispatch(setLoading(true));

      try {
        const subscription = await getActiveSubscription(upgradeJwt);
        dispatch(setActiveSubscription(subscription));
        dispatch(setDonorInfo(mapSubscriptionToDonorInfo(subscription)));
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load active subscription';
        logger.error('Failed to load active subscription', { formKey, formId, formSlug, error: err });
        dispatch(setError(errorMessage));
      } finally {
        dispatch(setLoading(false));
      }
    };

    void loadSubscription();
  }, [formId, formSlug, mode, upgradeJwt, dispatch]);

  useEffect(() => {
    const loadBillingDonation = async () => {
      if (mode !== 'billing-update') return;

      if (!receiptNumber) {
        dispatch(setError('Missing receipt information'));
        return;
      }

      // Require either updateToken or withAuth
      if (!updateToken && !billingUpdateWithAuth) {
        dispatch(setError('Missing update credentials'));
        return;
      }

      dispatch(setLoading(true));
      try {
        const donation = await getDonationReceipt(receiptNumber, billingUpdateWithAuth ? { withAuth: true } : { updateToken });
        const updateInfoForm = await getDonationFormBySlug(BILLING_UPDATE_FORM_SLUG);

        // Billing update should always render with the dedicated update-info form.
        let formWithOverrides = updateInfoForm;
        if (paymentOverrides) {
          formWithOverrides = applyPaymentOverrides(formWithOverrides, paymentOverrides);
        }
        const donationWithOverrides = { ...donation, form: formWithOverrides };

        dispatch({
          type: 'SET_BILLING_DONATION',
          payload: { 
            donation: donationWithOverrides, 
            updateToken: updateToken ?? null, 
            withAuth: billingUpdateWithAuth,
            initialStep: billingUpdateInitialStep,
          },
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load donation receipt';
        logger.error('Failed to load donation receipt', { receiptNumber, error: err });
        dispatch(setError(errorMessage));
      } finally {
        dispatch(setLoading(false));
      }
    };

    void loadBillingDonation();
  }, [mode, receiptNumber, updateToken, billingUpdateWithAuth, billingUpdateInitialStep, paymentOverrides, dispatch]);

  // Pre-populate donor info when memberId is provided.
  // Prefer the directly-supplied prefillDonor (self-service flow with auth context's member);
  // otherwise fall back to fetching /donate/v1/members/:id (admin flow).
  useEffect(() => {
    if (mode !== 'default') return;

    if (prefillDonor) {
      dispatch(setDonorInfo(mapMemberDetailToDonorInfo(prefillDonor)));
      return;
    }

    if (!memberId) return;

    const loadMemberDetails = async () => {
      try {
        const memberDetail = await getMemberDetail(memberId);
        dispatch(setDonorInfo(mapMemberDetailToDonorInfo(memberDetail)));
        logger.info('Pre-populated donor info from member', { memberId });
      } catch (err) {
        // Non-fatal: user can still fill in the form manually
        logger.warn('Failed to load member details for pre-population', { memberId, error: err });
      }
    };

    void loadMemberDetails();
  }, [memberId, prefillDonor, mode, dispatch]);

  // Sync memberId prop changes to state (admin flow)
  useEffect(() => {
    if (mode !== 'default') return;
    dispatch(setMemberId(memberId ?? null));
  }, [memberId, mode, dispatch]);

  // Sync utmCampaign prop changes to state (for admin flow where campaign can be changed)
  useEffect(() => {
    dispatch(setUtmCampaign(utmCampaign));
  }, [utmCampaign, dispatch]);

  // Check for Apple Pay support once on mount
  useEffect(() => {
    const checkApplePaySupport = async () => {
      if (!state.formDetails?.apple_pay) {
        dispatch(setCanMakeApplePay(false));
        return;
      }

      try {
        const canMake = await canMakeApplePayPayments();
        dispatch(setCanMakeApplePay(canMake));
      } catch (error) {
        logger.error('Failed to check Apple Pay support', { error });
        dispatch(setCanMakeApplePay(false));
      }
    };

    void checkApplePaySupport();
  }, [state.formDetails?.apple_pay, dispatch]);

  const content = React.useMemo(
    () => ({ headline: resolveHeadline(state), description: resolveDescription(state) }),
    [state]
  );

  return <FormContext.Provider value={{ state, dispatch, content, paymentOverrides }}>{children}</FormContext.Provider>;
};

export const useFormContext = (): FormContextValue => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};

